import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { WorkspaceBranding } from '@/types'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export type WorkspaceBadgeSurface = 'light' | 'dark'
export type WorkspaceBadgeKind = 'icon' | 'logo'

export interface WorkspaceBadgeCandidate {
  src: string
  surface: WorkspaceBadgeSurface
  kind: WorkspaceBadgeKind
}

interface RawWorkspaceBadgeCandidate {
  src: string | null | undefined
  enabled: boolean | null | undefined
  surface: WorkspaceBadgeSurface
  kind: WorkspaceBadgeKind
}

function resolveWorkspaceBrandAsset(
  src: string,
  workspaceId: number | string | null | undefined,
): string {
  let normalized = src.trim()
  const id = workspaceId == null ? '' : String(workspaceId)

  // Site icons are historically stored as "<workspace id>/assets/...",
  // while the shared in-app resolver expects "assets/...". Accept both the
  // historical and current forms without changing already-resolved URLs.
  if (id) {
    const prefixes = [
      `${id}/`,
      `/${id}/`,
      `workspaces/${id}/`,
      `/workspaces/${id}/`,
    ]
    const prefix = prefixes.find(candidate => normalized.startsWith(candidate))
    if (prefix) {
      const remainder = normalized.slice(prefix.length)
      if (remainder.startsWith('assets/')) normalized = remainder
    }
  }

  return resolveMediaUrl(normalized, workspaceId)
}

export function resolveWorkspaceBadgeCandidates(
  branding: Partial<WorkspaceBranding> | null | undefined,
  workspaceId: number | string | null | undefined,
): WorkspaceBadgeCandidate[] {
  if (!branding) return []

  const ordered: RawWorkspaceBadgeCandidate[] = [
    {
      src: branding.appleTouchIcon,
      enabled: branding.hasAppleTouchIcon,
      surface: 'light',
      kind: 'icon',
    },
    {
      src: branding.favIcon,
      enabled: branding.hasFavIcon,
      surface: 'light',
      kind: 'icon',
    },
    {
      src: branding.favIconOnDark,
      enabled: branding.hasFavIconOnDark,
      surface: 'dark',
      kind: 'icon',
    },
    {
      src: branding.logoUrl,
      enabled: branding.hasLogo,
      surface: 'light',
      kind: 'logo',
    },
    {
      src: branding.logoOnDarkUrl,
      enabled: branding.hasLogoOnDark,
      surface: 'dark',
      kind: 'logo',
    },
  ]

  const seen = new Set<string>()
  const resolved: WorkspaceBadgeCandidate[] = []

  for (const candidate of ordered) {
    // An explicit false means the stored path is stale/disabled. Null or an
    // absent flag remains compatible with older workspaces where the URL was
    // saved before the matching boolean existed.
    if (candidate.enabled === false || !candidate.src?.trim()) continue
    const src = resolveWorkspaceBrandAsset(candidate.src, workspaceId)
    if (!src || seen.has(src)) continue
    seen.add(src)
    resolved.push({ src, surface: candidate.surface, kind: candidate.kind })
  }

  return resolved
}

export function useWorkspaceBadge(
  branding: MaybeRefOrGetter<Partial<WorkspaceBranding> | null | undefined>,
  workspaceId: MaybeRefOrGetter<number | string | null | undefined>,
) {
  const candidates = computed(() => resolveWorkspaceBadgeCandidates(
    toValue(branding),
    toValue(workspaceId),
  ))
  const candidateIndex = ref(0)

  // A workspace switch or successful branding upload starts selection again
  // from the highest-priority image.
  watch(candidates, () => {
    candidateIndex.value = 0
  })

  const candidate = computed(() => candidates.value[candidateIndex.value] ?? null)
  const src = computed(() => candidate.value?.src ?? '')
  const surface = computed<WorkspaceBadgeSurface>(() => candidate.value?.surface ?? 'dark')
  const kind = computed<WorkspaceBadgeKind>(() => candidate.value?.kind ?? 'icon')

  function handleImageError(): void {
    if (candidateIndex.value < candidates.value.length) candidateIndex.value += 1
  }

  return {
    candidates,
    src,
    surface,
    kind,
    handleImageError,
  }
}
